import React, { useState, useRef, useEffect, useMemo, useCallback, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { FiSearch, FiInfo } from "react-icons/fi";
import { IoFilter } from "react-icons/io5";
import { MdClose } from "react-icons/md";
import Input from "../Input/Input";
import DatePickerFilter from "../DatePickerFilter/DatePickerFilter";
import Chip from "../kit/Chip";
import IconButton from "../kit/IconButton";
import { backendUrl } from "../../api/constants";
import { StableTagProvider, StableTagList } from "./StableTagFilters";
import "./GlobalSearchFilters.css";

// Stable TagImage component to prevent flickering
const TagImage = React.memo(({ serviceId, serviceName, iconUrl }) => {
  return (
    <img 
      src={iconUrl} 
      alt={serviceName} 
      className="w-4 h-4 object-contain"
      style={{
        imageRendering: 'crisp-edges'
      }}
    />
  );
});

const GlobalSearchFilters = React.forwardRef(({
  // Search configuration
  searchValue = "",
  onSearchChange,
  searchPlaceholder = "search...",
  searchHint,
  onSearchHintClick,
  
  // Tabs configuration
  showSearchTabs = false, // Controls search tabs visibility (events/venues/accounts)
  tabs = [],
  activeTab,
  onTabChange,

  // Date filter configuration
  showDateFilter = false,
  dateFilter,
  onDateFilterChange,
  defaultDateFilter,
  datePickerRef, // New: ref to access DatePickerFilter methods
  
  // Tags configuration
  showTags = false,
  knownServices = {},
  activeTags = [],
  onTagToggle,
  
    // Tab-specific filter configuration
  tabFilterConfig = {},

  // Loading states
  searchLoading = false,

  // Callbacks
  onFiltersExpandedChange,
  onHeightChange,

  // Styling
  className = "",
  allowOverflow = false,
  
  // Suggestions
  autoSuggestions = [],
  onSuggestionClick,
  onSuggestionInteraction,

  // Visibility control
  isVisible = true,
}, ref) => {
  // Debug: Track render count
  const renderCount = useRef(0);
  renderCount.current += 1;
  
  if (renderCount.current > 10) {
    // alert(`🚨 GlobalSearchFilters render #${renderCount.current} - Potential infinite loop!`);
  }

  const searchInputRef = useRef();
  const containerRef = useRef();
  const collapsibleRef = useRef();
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [showTagsInfo, setShowTagsInfo] = useState(false);
  const [dismissedSearchTabsQuery, setDismissedSearchTabsQuery] = useState("");
  const tagsInfoRef = useRef();
  const trimmedSearchValue = searchValue?.trim?.() || "";
  const shouldShowSearchTypeTabs =
    showSearchTabs &&
    tabs.length > 0 &&
    trimmedSearchValue.length > 0 &&
    dismissedSearchTabsQuery !== trimmedSearchValue;

  // Expose methods to parent component via ref
  useImperativeHandle(ref, () => ({
    expandFilters: () => {
      console.log('GlobalSearchFilters.expandFilters() called');
      setFiltersExpanded(true);
    },
    collapseFilters: () => {
      console.log('GlobalSearchFilters.collapseFilters() called');
      setFiltersExpanded(false);
    },
    toggleFilters: () => {
      console.log('GlobalSearchFilters.toggleFilters() called');
      setFiltersExpanded(prev => !prev);
    },
  }));

  useEffect(() => {
    if (!trimmedSearchValue && dismissedSearchTabsQuery) {
      setDismissedSearchTabsQuery("");
    }
  }, [trimmedSearchValue, dismissedSearchTabsQuery]);

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Tab' && autoSuggestions?.length > 0) {
      e.preventDefault();
      if (onSuggestionClick) {
        onSuggestionClick(autoSuggestions[0]);
      }
    }
    
    // Handle Enter key to dismiss keyboard on iOS
    if (e.key === 'Enter') {
      e.preventDefault();
      if (trimmedSearchValue) {
        setDismissedSearchTabsQuery(trimmedSearchValue);
      }
      // Blur the input to dismiss the keyboard
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  };

  // Create a stable callback that won't cause re-renders but sees current values
  const stableTagClickHandler = useCallback((tagId) => {
    if (onTagToggle) {
      onTagToggle(tagId);
    }
  }, [onTagToggle]);
  
  const handleTagClick = (tagId) => {
    // alert(`🏷️ Tag clicked: ${tagId}`);
    if (onTagToggle) {
      // alert(`📞 Calling onTagToggle(${tagId})`);
      onTagToggle(tagId);
    }
  };

  const handleSearchTypeSelect = (tabValue, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (onTabChange) {
      onTabChange(tabValue);
    }
  };

  const handleClearSearch = () => {
    const defaultTab = tabs[0]?.value || 'event';

    onSearchChange?.({ target: { value: '' } });
    if (onTabChange && activeTab !== defaultTab) {
      onTabChange(defaultTab);
    }
  };

  // Memoize service URLs to prevent flickering
  const serviceUrls = useMemo(() => {
    const urls = {};
    Object.entries(knownServices || {}).forEach(([id, service]) => {
      urls[id] = `${backendUrl}/${service.icon}`;
    });
    return urls;
  }, [knownServices]);

  // Memoize filtered services to prevent recreation
  const filteredServices = useMemo(() => {
    // alert(`🔄 FilteredServices memo recalculated`);
    return Object.entries(knownServices || {})
      .filter(([_, service]) => service.filterActive !== false);
  }, [knownServices]);

  const TagFilter = React.memo(({ disabled, className, children, active, onClick, id }) => {
    return (
      <div
        id={id}
        onClick={() => {
          if (disabled) return;
          onClick && onClick();
        }}
        className={`text-center duration-300 font-['Primary-Medium'] text-kit-xs md:text-kit-sm flex justify-start items-center py-kit-4 md:py-kit-6 px-kit-12 md:px-kit-16 rounded-kit-full cursor-pointer border-[var(--inyc-input-border-color)] border-[1px] ${
          active
            ? "bg-[var(--inyc-secondary)] active !text-[--inyc-secondary-text-color]"
            : "bg-[var(--inyc-primary)]"
        } ${className} 
        ${disabled ? "opacity-50 pointer-events-none" : ""}
        `}
      >
        {children}
      </div>
    );
  });

  const toggleFilters = () => {
    const newExpanded = !filtersExpanded;
    setFiltersExpanded(newExpanded);
    // Notify parent component of the change
    if (onFiltersExpandedChange) {
      onFiltersExpandedChange(newExpanded);
    }
  };

  // Check if any filters are active (not default state)
  const hasActiveFilters = () => {
    // Check if date filter is active (excluding default value)
    // Date filter is only active if it's set AND different from the default
    // We need to compare the actual date values, not just object references
    let hasDateFilter = false;
    if (dateFilter && defaultDateFilter) {
      const dateFilterStart = dateFilter.start?.getTime?.();
      const dateFilterEnd = dateFilter.end?.getTime?.();
      const defaultStart = defaultDateFilter.start?.getTime?.();
      const defaultEnd = defaultDateFilter.end?.getTime?.();
      
      hasDateFilter = (dateFilterStart !== defaultStart) || (dateFilterEnd !== defaultEnd);
    }
    
    // Check if any tags are active
    const hasActiveTags = activeTags && activeTags.length > 0;
    
    // Check if active tab is not the default (assuming first tab or 'event' is default)
    const hasNonDefaultTab = activeTab && activeTab !== 'event' && activeTab !== tabs[0]?.value;
    const result = hasDateFilter || hasActiveTags || hasNonDefaultTab;
    
    return result;
  };

  // Helper functions to determine what filters to show for current tab
  const shouldShowDateFilter = () => {
    if (!activeTab || !tabFilterConfig[activeTab]) {
      return showDateFilter; // fallback to prop
    }
    return tabFilterConfig[activeTab].showDateFilter ?? showDateFilter;
  };

  const shouldShowTags = () => {
    if (!activeTab || !tabFilterConfig[activeTab]) {
      return showTags; // fallback to prop
    }
    return tabFilterConfig[activeTab].showTags ?? showTags;
  };

  const getTabSpecificFilters = () => {
    if (!activeTab || !tabFilterConfig[activeTab]) {
      return [];
    }
    return tabFilterConfig[activeTab].customFilters || [];
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Handle tags info tooltip
      const isOutsideTagsInfo = tagsInfoRef.current && !tagsInfoRef.current.contains(event.target);
      const isOutsideTagsTooltip = !event.target.closest('.tags-info-tooltip');
      
      if (isOutsideTagsInfo && isOutsideTagsTooltip) {
        // alert(`❌ Closing tags info`);
        setShowTagsInfo(false);
      }
    };

    if (showTagsInfo) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTagsInfo]);

  // Track height changes and notify parent
  useEffect(() => {
    if (!containerRef.current || !onHeightChange) {
      return;
    }

    let lastReportedHeight = null;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const height = entry.contentRect.height;
        const reportHeight = isVisible ? height : 0;
        
        // Only update if height actually changed significantly (prevent sub-pixel differences)
        const hasSignificantChange = lastReportedHeight === null || Math.abs(lastReportedHeight - reportHeight) >= 1;
        
        if (hasSignificantChange) {
          lastReportedHeight = reportHeight;
          onHeightChange(reportHeight);
        }
      }
    });

    resizeObserver.observe(containerRef.current);

    // Initial height measurement
    const initialHeight = containerRef.current.offsetHeight;
    const initialReportHeight = isVisible ? initialHeight : 0;
    lastReportedHeight = initialReportHeight;
    onHeightChange(initialReportHeight);

    return () => {
      resizeObserver.disconnect();
    };
  }, [onHeightChange, filtersExpanded, isVisible]); // Re-run when expansion state or visibility changes

  return (
    <>
      <div 
        ref={containerRef} 
        className={`w-full grid transition-all duration-300 ease-in-out ${
          isVisible
            ? `grid-rows-[1fr] opacity-100 ${allowOverflow ? "overflow-visible pb-kit-4" : "overflow-hidden"}`
            : 'grid-rows-[0fr] opacity-0 overflow-hidden'
        }`}
      >
        <div className={`global-search-filters ${className} min-h-0 ${!isVisible ? 'pointer-events-none' : ''}`}>
        

        {/* Search Bar with Filter Toggle */}
        <div className="search-filters-search-section">
          <div className="search-filters-input-row">
            <div className="relative flex-1">
              <Input
                placeHolder={searchPlaceholder}
                Ref={searchInputRef}
                onChange={onSearchChange}
                value={searchValue}
                hint={searchHint}
                icon={<FiSearch size={16} />}
                onHintClick={onSearchHintClick}
                onKeyDown={handleSearchKeyDown}
                className="search-filters-input"
              />

              {searchValue && searchValue.trim().length > 0 && (
                <IconButton
                  size="xs"
                  label="clear search"
                  icon={<MdClose />}
                  className="absolute top-1/2 right-[0.5rem] -translate-y-1/2 z-10"
                  onClick={handleClearSearch}
                />
              )}
            </div>
            <Chip
              aria-label="filters"
              aria-expanded={filtersExpanded}
              selected={Boolean(filtersExpanded || hasActiveFilters())}
              icon={<IoFilter size={16} />}
              onClick={toggleFilters}
            />
          </div>

          {showSearchTabs && tabs.length > 0 && (
            <div
              className={`search-type-tabs-section ${shouldShowSearchTypeTabs ? 'visible' : ''}`}
              aria-label="search type"
              aria-hidden={!shouldShowSearchTypeTabs}
            >
              <div className="search-type-tabs">
                {tabs.map((tab) => (
                  <Chip
                    key={tab.value}
                    selected={activeTab === tab.value}
                    onClick={(e) => handleSearchTypeSelect(tab.value, e)}
                    tabIndex={shouldShowSearchTypeTabs ? 0 : -1}
                  >
                    {tab.label}
                  </Chip>
                ))}
              </div>
            </div>
          )}
          
          {/* Auto suggestions */}
          {autoSuggestions && autoSuggestions.length > 0 && (
            <div
              className="inyc-menu is-field search-filters-suggestions"
              onMouseEnter={() => onSuggestionInteraction?.(true)}
              onMouseLeave={() => onSuggestionInteraction?.(false)}
            >
              {autoSuggestions.map((suggestion, index) => (
                <button
                  type="button"
                  key={index}
                  className="inyc-menu-item"
                  onClick={() => onSuggestionClick?.(suggestion)}
                >
                  {suggestion.fullName?.length > 20 
                    ? suggestion.fullName.slice(0, 20) + "..." 
                    : suggestion.fullName || suggestion
                  }
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Collapsible Filters */}
        <div
          ref={collapsibleRef}
          className={`w-full grid mt-[-1.2rem] md:mt-[-0.25rem] overflow-hidden duration-300 ${
            filtersExpanded ? "grid-rows-[1fr] mb-kit-4" : "grid-rows-[0fr]"
          } gap-kit-8`}
        >
          <div className={`min-h-0 search-filters-content ${filtersExpanded ? 'visible' : ''}`}>

            {/* Date Filter */}
            {shouldShowDateFilter() && (
              <div className="search-filters-date-section">
                <div className="filter-option-flex h-fit date-filter duration-300">
                  <Chip
                    selected={dateFilter === "now"}
                    onClick={() => onDateFilterChange?.(dateFilter === "now" ? null : "now")}
                  >
                    now
                  </Chip>
                  <Chip
                    selected={dateFilter === "soon"}
                    onClick={() => onDateFilterChange?.(dateFilter === "soon" ? null : "soon")}
                  >
                    soon
                  </Chip>
                  <Chip
                    selected={dateFilter === "later"}
                    onClick={() => onDateFilterChange?.(dateFilter === "later" ? null : "later")}
                  >
                    later
                  </Chip>
                  <Chip
                    selected={dateFilter === "late night"}
                    onClick={() => onDateFilterChange?.(dateFilter === "late night" ? null : "late night")}
                  >
                    late night
                  </Chip>
                  <DatePickerFilter
                    ref={datePickerRef}
                    currentDate={dateFilter}
                    setCurrentDate={onDateFilterChange}
                    defaultDate={defaultDateFilter}
                  />
                </div>
                <div className="date-filter-divider" />
              </div>
            )}

            {/* Tags */}
            {shouldShowTags() && Object.keys(knownServices || {}).length > 0 && (
              <div className="search-filters-tags-section relative">
                <StableTagProvider knownServices={knownServices}>
                  <div className="flex flex-wrap gap-kit-8 justify-center items-center">
                    <IconButton
                      ref={tagsInfoRef}
                      label="about these filters"
                      icon={<FiInfo />}
                      className="tags-info-icon absolute right-0 top-1/2"
                      onClick={() => setShowTagsInfo(!showTagsInfo)}
                    />
                    <StableTagList 
                      activeTags={activeTags}
                      onTagClick={stableTagClickHandler}
                    />
                  </div>
                </StableTagProvider>
                
                {/* Info tooltip */}
                {showTagsInfo && tagsInfoRef.current &&
                  createPortal(
                    <div
                      className="tags-info-tooltip"
                      style={{
                        position: 'fixed',
                        top: tagsInfoRef.current.getBoundingClientRect().bottom + 4,
                        right: Math.max(16, window.innerWidth - tagsInfoRef.current.getBoundingClientRect().right),
                        zIndex: 10000
                      }}
                    >
                      <div className="tags-info-content">
                        festival and organization filter buttons.
                        <br />
                        reach out and we'll make one for you!
                      </div>
                    </div>,
                    document.body
                  )
                }
              </div>
            )}

            {/* Tab-Specific Custom Filters */}
            {getTabSpecificFilters().map((filterSection, index) => (
              <div key={index} className="search-filters-custom-section">
                {filterSection.render && filterSection.render()}
              </div>
            ))}
          </div>
        </div>
        </div>
      </div>
      
    </>
  );
});

// Set display name for debugging
GlobalSearchFilters.displayName = 'GlobalSearchFilters';

// Memoize GlobalSearchFilters to prevent unnecessary re-renders
const MemoizedGlobalSearchFilters = React.memo(GlobalSearchFilters, (prevProps, nextProps) => {
  // Only re-render if essential props change
  return (
    prevProps.searchValue === nextProps.searchValue &&
    prevProps.activeTab === nextProps.activeTab &&
    prevProps.dateFilter === nextProps.dateFilter &&
    prevProps.activeTags === nextProps.activeTags &&
    prevProps.isVisible === nextProps.isVisible &&
    prevProps.searchLoading === nextProps.searchLoading
  );
});

export default MemoizedGlobalSearchFilters;